import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  theme,
  Button,
  Form,
  Input,
  message,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { calculateGreenWave, getTrafficLights, createSignal } from "../apis";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  MapContainer,
  TileLayer,
  useMap,
  FeatureGroup,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import Semafori from "../Onde/Modals/Semafori";
import "../styles/pages/onde.css";
import CarButton from "./../Assets/icons/car_button.png";
import SelectionIcon from "./../Assets/icons/selection.png";
import ResetIcon from "./../Assets/icons/reset.png";
// Import new components
import SimulationVisualization from "../components/Onde/SimulationVisualization";
import ControlloOndeVerde from "../components/Onde/ControlloOndeVerde";
import SelectedTrafficCard from "../components/Onde/SelectedTrafficCard";
import TrafficLightMarkers from "../components/Onde/TrafficLightMarkers";
import MapControls from "../components/Onde/MapControls";
import SelectionPopup from "../components/Onde/SelectionPopup";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});


// Map click handler component for adding manual points
function MapClickHandler({ isAddingManualPoint, isDrawing, onMapClick }) {
  const map = useMap();

  useEffect(() => {
    const handleClick = (e) => {
      // Only add manual point if the button is active and NOT currently drawing
      if (isAddingManualPoint && !isDrawing) {
        onMapClick(e.latlng);
      }
    };

    map.on('click', handleClick);

    return () => {
      map.off('click', handleClick);
    };
  }, [map, isAddingManualPoint, isDrawing, onMapClick]);

  return null;
}

// Separate component for EditControl to prevent re-render issues
const DrawControl = React.memo(({ onCreated, onEdited, onDeleted }) => {
  return (
    <EditControl
      position="topleft"
      onCreated={onCreated}
      onEdited={onEdited}
      onDeleted={onDeleted}
      draw={{
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: false,
        polyline: false,
        polygon: {
          allowIntersection: false,
          drawError: {
            color: '#e74c3c',
            message: '<strong>Errore:</strong> i bordi non possono incrociarsi!'
          },
          shapeOptions: {
            color: '#A1E430',
            weight: 3,
            opacity: 0.8,
            fillOpacity: 0.2
          }
        }
      }}
      edit={{
        edit: {
          selectedPathOptions: {
            color: '#FE2D46',
            opacity: 0.8,
            weight: 3
          }
        },
        remove: true
      }}
    />
  );
});

function Onde() {
  const [messageApi, contextHolder] = message.useMessage();
  const {
    token: {
      colorPrimary,
      colorSuccess,
      colorError,
    },
  } = theme.useToken();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [loadingModal, setLoadingModal] = useState(false);
  const [result, setResult] = useState({
    offset_coppie: [],
    risultati: [],
  });
  const [animationActive, setAnimationActive] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [activeSignals, setActiveSignals] = useState([]);
  const [animationSpeed, setAnimationSpeed] = useState(50);
  const [advance, setAdvance] = useState(2);
  const [partenza, setPartenza] = useState(0);
  const [viewMode, setViewMode] = useState("1d");
  const [mapView, setMapView] = useState("map"); // Add map view state (map/satellite)
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(0);
  const chartRef = useRef(null);
  const mapRef = useRef(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const [isFromMap, setIsFromMap] = useState(false);
  var [containerHeight, setContainerHeight] = useState(500); // Set default height
  const [mapCenter, setMapCenter] = useState([44.432401344516244, 8.88945112875312]); // Default center
  const [initialMapZoom] = useState(15); // Store initial zoom level
  const [showSimulation, setShowSimulation] = useState(false);

  // Define offset for graph axes
  const offsetX = 50; // Pixels to offset the 0 point on the X-axis for 1D
  const offsetY = 50; // Pixels to offset the 0 point on the Y-axis for 2D
  // Dynamic yScale logic: reduce yScale if any signal.distanza > 400
  const maxSignalDistance = Math.max(
    ...result.risultati.map((signal) => signal.distanza),
    0
  );
  const yScale =
    maxSignalDistance > 400 ? Math.max(0.3, 300 / maxSignalDistance) : 0.75;
  const [formValues, setFormValues] = useState({
    vel_kmh: 15,
    partenza_A: 0,
    anticipa_s: 2,
    semafori: [
      {
        nome: "S1",
        distanza: 100,
        durata_verde: 20,
        lunghezza_attraversamento: 10,
      },
    ],
  });

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({
      vel_kmh: 15,
      partenza_A: 0,
      anticipa_s: 2,
      semafori: [
        {
          nome: "S1",
          distanza: 100,
          durata_verde: 20,
          lunghezza_attraversamento: 10,
        },
      ],
    });
    setResult({
      offset_coppie: [],
      risultati: [],
    });
    setTrafficLights([]);
    localStorage.removeItem("trafficLights");
    localStorage.removeItem("formValues");
    setAnimationActive(false);
    setFormValues({
      vel_kmh: 15,
      partenza_A: 0,
      anticipa_s: 2,
      semafori: [
        {
          id: "s1",
          nome: "S1",
          distanza: 100,
          durata_verde: 20,
          lunghezza_attraversamento: 10,
        },
      ],
    });
    setManualSelect(false);
    setSelectedTraffic([]);
    localStorage.removeItem("response");
  };

  // Load traffic lights from API with bbox
  const handleLoadTrafficLights = async () => {
    try {
      let bbox = undefined;

      // Check if user has drawn a polygon
      if (drawnPolygon) {
        // Get bounds from the drawn polygon
        const bounds = drawnPolygon.getBounds();
        bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
        console.log("Loading traffic lights with drawn polygon bbox:", bbox);
      } else {
        // If no polygon drawn, show info message
        messageApi.info("Per favore, disegna un poligono sulla mappa prima di caricare i semafori");
        return;
      }

      setIsLoadingCarica(true);
      const response = await getTrafficLights(bbox);
      // Access the traffic_lights array from the response
      const data = response?.traffic_lights || response || [];

      if (data && data.length > 0) {
        // Map the data to use 'lon' as 'lng' for consistency
        const mappedData = data.map(light => ({
          ...light,
          lng: light.lon || light.lng,
          latitude: light.lat,
          longitude: light.lon || light.lng
        }));

        setTrafficLights(mappedData);
        localStorage.setItem("trafficLights", JSON.stringify(mappedData));
        messageApi.success(`Caricati ${data.length} semafori dall'area selezionata`);
      } else {
        messageApi.info("Nessun semaforo trovato nell'area del poligono");
      }
    } catch (error) {
      console.error("Error loading traffic lights:", error);
      messageApi.error("Errore nel caricamento dei semafori");
    } finally {
      setIsLoadingCarica(false);
    }
  };

  // Clear traffic lights and polygon
  const handleClearTrafficLights = () => {
    setTrafficLights([]);
    setSelectedTraffic([]);
    setDrawnPolygon(null);
    localStorage.removeItem("trafficLights");

    // Clear results to close ControlloOndeVerde card
    setResult({ offset_coppie: [], risultati: [] });
    localStorage.removeItem("response");

    // Clear all drawn shapes from the map
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }

    messageApi.info("Semafori e poligono rimossi");
  };

  // Handle polygon creation - wrapped in useCallback to prevent re-creation
  const handlePolygonCreated = useCallback((e) => {
    const { layer } = e;

    // Clear previous polygon
    if (featureGroupRef.current) {
      featureGroupRef.current.eachLayer((existingLayer) => {
        if (existingLayer !== layer) {
          featureGroupRef.current.removeLayer(existingLayer);
        }
      });
    }

    setDrawnPolygon(layer);
    setIsDrawing(false);
    messageApi.success("Poligono creato! Ora puoi caricare i semafori");
  }, [messageApi]);

  // Handle polygon edit - wrapped in useCallback to prevent re-creation
  const handlePolygonEdited = useCallback((e) => {
    const layers = e.layers;
    layers.eachLayer((layer) => {
      setDrawnPolygon(layer);
    });
    setIsDrawing(false);
    messageApi.info("Poligono modificato");
  }, [messageApi]);

  // Handle polygon deletion - wrapped in useCallback to prevent re-creation
  const handlePolygonDeleted = useCallback(() => {
    setDrawnPolygon(null);
    setIsDrawing(false);
    messageApi.info("Poligono eliminato");
  }, [messageApi]);

  // Listen to Leaflet draw events on the map
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const handleDrawStartEvent = () => {
      setIsDrawing(true);
      setIsAddingManualPoint(false);
    };

    const handleDrawStopEvent = () => {
      setIsDrawing(false);
    };

    // Listen to Leaflet.draw events
    map.on('draw:drawstart', handleDrawStartEvent);
    map.on('draw:drawstop', handleDrawStopEvent);
    map.on('draw:editstart', handleDrawStartEvent);
    map.on('draw:editstop', handleDrawStopEvent);
    map.on('draw:deletestart', handleDrawStartEvent);
    map.on('draw:deletestop', handleDrawStopEvent);

    return () => {
      map.off('draw:drawstart', handleDrawStartEvent);
      map.off('draw:drawstop', handleDrawStopEvent);
      map.off('draw:editstart', handleDrawStartEvent);
      map.off('draw:editstop', handleDrawStopEvent);
      map.off('draw:deletestart', handleDrawStartEvent);
      map.off('draw:deletestop', handleDrawStopEvent);
    };
  }, [mapRef.current]);

  useEffect(() => {
    const updateSize = () => {
      if (chartRef.current) {
        setChartSize({
          width: chartRef.current.clientWidth,
          height: chartRef.current.clientHeight,
        });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);

    return () => window.removeEventListener("resize", updateSize);
  }, [chartRef.current]);

  useEffect(() => {
    // Add a small delay to ensure the modal is fully rendered
    if (showSimulation && viewMode === '2d') {
      setTimeout(() => {
        var lineContainer = document.querySelector(".onde-green-wave-container");
        var chartContainer = document.querySelector(".onde-chart-ref");

        // Try to get height from either container
        var height = lineContainer?.offsetHeight || chartContainer?.offsetHeight || 500;

        // Ensure we have a reasonable height
        if (height < 100) {
          height = 500; // Default height if containers are not properly sized yet
        }

        setContainerHeight(height);
      }, 150); // Slightly longer delay to ensure full render
    }
  }, [viewMode, showSimulation]);

  const { maxTime, maxDistance, totalPathDistance, firstSignalDistance } = useMemo(() => {
    const maxTime = Math.max(
      ...result.risultati.map((item) => item.fine_verde),
      200
    );
    const maxDistance = Math.max(
      ...result.risultati.map((item) => item.distanza),
      100
    );
    // Calculate total cumulative distance for the full path
    const totalPathDistance = result.risultati.reduce((acc, signal) => acc + signal.distanza, 0);
    // First signal's distance (where the car should start)
    const firstSignalDistance = result.risultati[0]?.distanza || 0;

    return { maxTime, maxDistance, totalPathDistance, firstSignalDistance };
  }, [result]);


  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setLoadingModal(true);
    form
      .validateFields()
      .then(async (values) => {
        setAnimationSpeed(values.vel_kmh);
        setAdvance(values.anticipa_s);
        setPartenza(values.partenza_A);
        setFormValues(values);
        const response = await calculateGreenWave({
          ...values,
          semafori: values.semafori.map((s) => {
            if (!s.id) return { ...s, id: s.nome };
            else return s;
          }),
        });
        if (response) {
          setResult(response);
          localStorage.setItem("response", JSON.stringify(response));
          setIsModalOpen(false);
          setActiveSignals([]);
        }
      })
      .catch((info) => {
      })
      .finally(() => {
        setLoadingModal(false);
      });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const toggleAnimation = () => {
    if (animationActive) {
      setAnimationActive(false);
      cancelAnimationFrame(animationRef.current);
    } else {
      startAnimation();
    }
  };

  const startAnimation = () => {
    setAnimationProgress(0);
    setActiveSignals([]);
    setAnimationActive(true);

    const speedMps = (animationSpeed * 10) / 36;
    // Car starts at first signal and travels to end
    const startPosition = firstSignalDistance;
    const duration = ((totalPathDistance - startPosition) / speedMps) * 1000;
    const startTime = Date.now();

    const animate = () => {
      const now = Date.now();
      if (now - lastUpdateRef.current < 16) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastUpdateRef.current = now;

      const elapsed = now - startTime;
      const rawProgress = elapsed / duration; // Raw progress for loop condition
      const progress = Math.min(rawProgress, 1); // Capped progress for car position

      setAnimationProgress(progress);

      // Car's current position: starts at first signal, ends at totalPathDistance
      const carPosition = startPosition + progress * (totalPathDistance - startPosition);

      // Signal is active if car is within 'advance' meters of it
      const newActiveSignals = [];
      for (var i = 0; i < result.risultati.length; i++) {
        var signal = result.risultati[i];
        // Calculate cumulative distance to this signal
        var signalCumulativeDistance = result.risultati
          .slice(0, i + 1)
          .reduce((acc, s) => acc + s.distanza, 0);

        // Signal is active when car is approaching (within advance distance) until car passes
        const approachDistance = advance * 2; // Distance before signal when it becomes active
        const signalStart = signalCumulativeDistance - approachDistance;
        const signalEnd = signalCumulativeDistance + signal.lunghezza_attraversamento;

        if (carPosition >= signalStart && carPosition <= signalEnd) {
          // Push id, semaforo name, and index for matching with map markers
          newActiveSignals.push(signal.id);
          if (signal.semaforo) {
            newActiveSignals.push(signal.semaforo);
          }
          // Also push index-based identifier for guaranteed matching
          newActiveSignals.push(`signal-index-${i}`);
        }
      }
      setActiveSignals(newActiveSignals);

      if (rawProgress < 1.2) {
        // Continue animation a bit past the end point
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setAnimationActive(false);
        setActiveSignals([]);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    // Load saved response
    if (localStorage.getItem("response")) {
      setResult(JSON.parse(localStorage.getItem("response")));
    }

    // Load saved traffic lights
    if (localStorage.getItem("trafficLights")) {
      try {
        const savedTrafficLights = JSON.parse(localStorage.getItem("trafficLights"));
        setTrafficLights(savedTrafficLights);
      } catch (error) {
        console.error("Error loading saved traffic lights:", error);
      }
    }

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const signalElements = useMemo(() => {
    return result.risultati.map((signal, index) => {
      const isActive = activeSignals.includes(signal.id);
      var distanza = result.risultati
        .slice(0, index + 1)
        .reduce((acc, signal) => acc + signal.distanza, 0);
      return (
        <React.Fragment key={index}>
          <Tooltip title={signal.semaforo}>
            <div
              className={`onde-signal-element ${isActive ? "active" : "inactive"
                } nome-${signal.semaforo}`}
              style={{
                left: `${offsetX + distanza * 1}px`,
                width: `${signal.lunghezza_attraversamento * 2}px`,
                borderColor: isActive ? colorSuccess : colorError,
              }}
            />
          </Tooltip>

          <div
            className={`onde-signal-label ${isActive ? "active" : "inactive"}`}
            style={{
              left: `${offsetX +
                distanza * 1 +
                (signal.lunghezza_attraversamento * 2) / 2
                }px`,
              color: isActive ? colorSuccess : colorError,
              width: `${signal.lunghezza_attraversamento * 2}px`,
            }}
          >
             <Tooltip title={signal.semaforo}>
              {signal.semaforo}
             </Tooltip>
            
          </div>
        </React.Fragment>
      );
    });
  }, [
    result.risultati,
    maxTime,
    activeSignals,
    animationProgress,
    colorError,
    colorPrimary,
    colorSuccess,
    offsetX,
  ]); // Add offsetX to dependencies

  useEffect(() => {
    form.setFieldsValue(formValues);
  }, [formValues]);

  var [manualSelect, setManualSelect] = useState(false);
  var [selectedTraffic, setSelectedTraffic] = useState([]);
  const [trafficLights, setTrafficLights] = useState([]);
  const [selectedSign, setSelectedSign] = useState(null);
  const [showSelectionPopup, setShowSelectionPopup] = useState(false);
  const [isAddingManualPoint, setIsAddingManualPoint] = useState(false);
  const [drawnPolygon, setDrawnPolygon] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const featureGroupRef = useRef(null);
  const [showControlloCard, setShowControlloCard] = useState(true);
  const [isLoadingCarica, setIsLoadingCarica] = useState(false);
  const [isLoadingTermina, setIsLoadingTermina] = useState(false);
  const [showSelectedCard, setShowSelectedCard] = useState(true);

  // Y Axis ticks logic: always show at least up to 500, then 550, 600, ... as needed
  const maxYAxis = Math.max(350, Math.ceil(maxSignalDistance / 50) * 50);
  const yAxisTicks = [];
  for (let t = 0; t <= maxYAxis; t += 50) {
    yAxisTicks.push(t);
  }

  return (
    <div className="onde-fullscreen-container">
      {contextHolder}

      {/* Fullscreen Map */}
      <div className="onde-fullscreen-map">
        <MapContainer
          center={mapCenter}
          zoom={initialMapZoom}
          className="map-container-fullscreen"
          ref={mapRef}
          whenCreated={(map) => {
            mapRef.current = map;
          }}
          zoomControl={false}
        >
          {mapView === "satellite" ? (
            <TileLayer
              maxZoom={18}
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Tiles &copy; Esri"
            />
          ) : (
            <TileLayer
              maxZoom={26}
              url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
          )}

          {/* Map Click Handler for Manual Points */}
          <MapClickHandler
            isAddingManualPoint={isAddingManualPoint}
            isDrawing={isDrawing}
            onMapClick={async (latlng) => {
              try {
                // Create a new manual traffic light
                const manualLight = {
                  id: `manual-${Date.now()}`,
                  name: `manual-${Date.now()}`,
                  lat: latlng.lat,
                  lng: latlng.lng,
                  latitude: latlng.lat,
                  longitude: latlng.lng,
                  isManual: true
                };

                // Try to create via API if available
                try {
                  const formData = new FormData();
                  formData.append('name', manualLight.name);
                  formData.append('lat', latlng.lat.toString());
                  formData.append('lng', latlng.lng.toString());
                  formData.append('type', 'manual');

                  // Attempt API call but don't fail if it doesn't work
                  await createSignal(formData);
                } catch (apiError) {
                  console.log('API call failed, using local storage:', apiError);
                }

                // Add to traffic lights locally regardless - use functional update to prevent stale state
                setTrafficLights(prevLights => {
                  const updatedLights = [...prevLights, manualLight];
                  // Save to localStorage to persist manual points
                  localStorage.setItem("trafficLights", JSON.stringify(updatedLights));
                  return updatedLights;
                });

                // Automatically select it
                setSelectedSign(manualLight);
                setShowSelectionPopup(true);
                setIsAddingManualPoint(false);

                messageApi.success('Manual traffic light added - please configure it');
              } catch (error) {
                messageApi.error('Failed to add traffic light');
                console.error('Error adding manual traffic light:', error);
              }
            }}
          />

          {/* Display Traffic Lights as Markers */}
          <TrafficLightMarkers
            trafficLights={trafficLights}
            selectedTraffic={selectedTraffic}
            setSelectedTraffic={setSelectedTraffic}
            manualSelect={manualSelect}
            setSelectedSign={setSelectedSign}
            setShowSelectionPopup={setShowSelectionPopup}
            activeSignals={activeSignals}
            animationActive={animationActive}
            setShowSelectedCard={setShowSelectedCard}
            messageApi={messageApi}
          />

          {/* Polygon Drawing Control */}
          <FeatureGroup ref={featureGroupRef}>
            <DrawControl
              onCreated={handlePolygonCreated}
              onEdited={handlePolygonEdited}
              onDeleted={handlePolygonDeleted}
            />
          </FeatureGroup>

          {/* Map Controls */}
          <MapControls
            mapView={mapView}
            setMapView={setMapView}
            drawnPolygon={drawnPolygon}
          />
        </MapContainer>
      </div>

      {/* Overlay Elements */}
      <div className="overlay-elements">
        {/* Top Control Bar */}
        <div className="onde-top-bar">
          <div className="onde-top-bar-inner">
            {/* Search Bar */}
            <div className="onde-search-container">
              <Input
                placeholder="Cerca località..."
                prefix={<EnvironmentOutlined />}
                className="onde-search-input"
                size="large"
              />
              {!drawnPolygon && trafficLights.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '60px',
                  left: '0',
                  background: 'rgba(161, 228, 48, 0.9)',
                  color: '#1a1a1a',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontWeight: '500',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  zIndex: 1000,
                  whiteSpace: 'nowrap'
                }}>
                  📍 Usa lo strumento di disegno in alto a destra per creare un poligono
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="onde-action-buttons" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {/* Additional Action Buttons - Settings and Manual Add - Moved to left with green color */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginRight: '12px',
                paddingRight: '12px',
              }}>
                {/* Settings Button - Green - Opens Configura Semafori modal */}
                <button
                  onClick={() => {
                    showModal();
                  }}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: '#353A0A',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(161, 228, 48, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(161, 228, 48, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(161, 228, 48, 0.3)';
                  }}
                  title="Settings"
                >
                  <SettingOutlined style={{color : "#fff", fontSize : "18px"}} />
                </button>

                {/* Manual Add Point Button - Green */}
                <button
                  onClick={() => {
                    setIsAddingManualPoint(!isAddingManualPoint);
                    if (!isAddingManualPoint) {
                      messageApi.info('Click on the map to add a manual traffic light');
                    }
                  }}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: "#353A0A",
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isAddingManualPoint
                      ? '0 2px 8px rgba(107, 148, 32, 0.4)'
                      : '0 2px 8px rgba(161, 228, 48, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(161, 228, 48, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = isAddingManualPoint
                      ? '0 2px 8px rgba(107, 148, 32, 0.4)'
                      : '0 2px 8px rgba(161, 228, 48, 0.3)';
                  }}
                  title={isAddingManualPoint ? "Cancel adding manual point" : "Add manual traffic light"}
                >
                  <PlusOutlined style={{color : "#fff", fontSize : "18px"}} />

                </button>
              </div>

              <Button
                type="primary"
                size="large"
                className="onde-action-button"
                onClick={() => {
                  if (result.risultati && result.risultati.length > 0) {
                    setShowSimulation(true);
                  } else {
                    showModal();
                  }
                }}
              >
                <img src={CarButton} />
                Simulazione
              </Button>
              <Button
                type="primary"
                size="large"
                className={`onde-action-button ${manualSelect ? 'active-selection' : ''}`}
                onClick={() => setManualSelect(!manualSelect)}
              >
                <img src={SelectionIcon} />
                {manualSelect ? 'Stop Selezione' : 'Seleziona'}
              </Button>
              <Tooltip
                title={trafficLights.length === 0 && !drawnPolygon ? "Seleziona un'area sulla mappa per caricare il traffico" : null}
              >
                <Button
                  type="primary"
                  size="large"
                  className="onde-action-button"
                  onClick={trafficLights.length > 0 ? handleClearTrafficLights : handleLoadTrafficLights}
                  disabled={(trafficLights.length === 0 && !drawnPolygon) || isLoadingCarica}
                  loading={isLoadingCarica}
                >
                  {!isLoadingCarica && <img src={ResetIcon} alt="" />}
                  {trafficLights.length > 0 ? 'Reset' : 'Carica'}
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Controllo dell'Onda Verde - Only show when we have results */}
        <ControlloOndeVerde
          result={result}
          setResult={setResult}
          visible={showControlloCard}
        />

        {/* Simulation Card - Only show when user clicks Simulazione */}
        <SimulationVisualization
          showSimulation={showSimulation}
          setShowSimulation={setShowSimulation}
          result={result}
          viewMode={viewMode}
          setViewMode={setViewMode}
          animationSpeed={animationSpeed}
          animationActive={animationActive}
          setAnimationActive={setAnimationActive}
          animationProgress={animationProgress}
          animationRef={animationRef}
          startAnimation={startAnimation}
          offsetX={offsetX}
          offsetY={offsetY}
          maxDistance={maxDistance}
          totalPathDistance={totalPathDistance}
          firstSignalDistance={firstSignalDistance}
          signalElements={signalElements}
          partenza={partenza}
          chartRef={chartRef}
          containerHeight={containerHeight}
          maxTime={maxTime}
          yAxisTicks={yAxisTicks}
          yScale={yScale}
          colorSuccess={colorSuccess}
          colorError={colorError}
        />
      </div>

      {/* Selection Popup */}
      <SelectionPopup
        selectedSign={selectedSign}
        showPopup={showSelectionPopup}
        onClose={() => {
          setShowSelectionPopup(false);
          setSelectedSign(null);
        }}
        onConfirm={(newTraffic) => {
          // Add to selected traffic
          setSelectedTraffic(prev => [...prev, newTraffic]);

          // Update the traffic light in the list
          setTrafficLights(prev => prev.map(light =>
            light.id === newTraffic.id ? newTraffic : light
          ));

          // Close popup
          setShowSelectionPopup(false);
          setSelectedSign(null);

          // Show the selected card again if it was hidden
          setShowSelectedCard(true);

          messageApi.success(`Semaforo "${newTraffic.nome}" aggiunto alla selezione`);
        }}
        messageApi={messageApi}
      />

      {/* Selected Traffic Lights Card */}
      <SelectedTrafficCard
        selectedTraffic={selectedTraffic}
        setSelectedTraffic={setSelectedTraffic}
        setFormValues={setFormValues}
        setIsFromMap={setIsFromMap}
        showModal={showModal}
        visible={showSelectedCard}
        setVisible={setShowSelectedCard}
        isLoadingTermina={isLoadingTermina}
        setIsLoadingTermina={setIsLoadingTermina}
        setManualSelect={setManualSelect}
      />

      {/* Modal for Semafori */}
      <Semafori
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        handleOk={handleOk}
        loadingModal={loadingModal}
        formValues={formValues}
        form={form}
        isFromMap={isFromMap}
      />
    </div>
  );
}

export default Onde;
